<template>
    <div>
        <template v-for="( key, index ) in headers">
            <span
                v-if="row[key]"
                class="table__body__row__text position--relative"
                :class="`table__${model}__col__${index}`"
                :key="index"
            >
                {{ row[key] || ''}}
            </span>
        </template>
        <span
            class="table__body__row__text position--relative"
            :class="`table__${model}__col__${headers.length}`"
            v-on="{
                ...( isInEditMode && {
                    mouseover: () => show.tooltip = true,
                    mouseleave: () => show.tooltip = false
                })
            }"
        >
            <button-with-icon
                v-if="row.revoked"
                :icon="{
                    component: () => import( /* webpackChunkName: 'IconActive' */ '@/components/bundles/layout/images/icons/IconActive' ),
                    class: `table__body__row__text__svg svg`
                }"
                :class="[
                    'table__body__row__text__button',
                    { 'disable': isInEditMode }
                ]"
                :aria-label="$t('accessibility.active')"
                :action="{
                    execute: !isInEditMode,
                    function: () => toggleModal( 'active' )
                }"
            />
            <button-with-icon
                v-else
                :icon="{
                    component: () => import( /* webpackChunkName: 'IconActive' */ '@/components/bundles/layout/images/icons/IconClose' ),
                    class: `table__body__row__text__svg svg`
                }"
                :class="[
                    'table__body__row__text__button',
                    { 'disable': isInEditMode }
                ]"
                :aria-label="$t('accessibility.disable')"
                :action="{
                    execute: !isInEditMode,
                    function: () => toggleModal( 'disable' )
                }"
            />
            <button-with-icon
                :icon="{
                    component: () => import( /* webpackChunkName: 'IconActive' */ '@/components/bundles/layout/images/icons/IconDelete' ),
                    class: `table__body__row__text__svg svg`
                }"
                :class="[
                    'table__body__row__text__button',
                    { 'disable': isInEditMode }
                ]"
                :aria-label="$t('accessibility.delete')"
                :action="{
                    execute: !isInEditMode,
                    function: () => toggleModal( 'delete' )
                }"
            />
            <tooltip
                v-show="show.tooltip"
                class="table__body__row__text__tooltip"
                direction="left"
                v-html="$t('global.exitEditMode')"
            />
        </span>
        <modal
            v-if="show.modal"
            :form="{
                class: 'modal__form--action display--flex',
                settings: {
                    typeOfPreparation: 'token',
                    ...( type === 'delete' &&{ submissionType: 'processDelete' })
                }

            }"
            :customSettings="{
                color,
                button: {
                    label: buttonText
                }
            }"
            :executeCloseAction="!loading"
            @close="toggleModal"
            :key="show.modal"
        >
            <template #modal__header >
                {{ $t(`global.${translate}`) }}
            </template>
            <template #modal__body >
                {{ $t(`warning.${translate}`) }}
            </template>
            <template #modal__info >
                <base-button
                    class="modal__content__button--negative background--transparent border--grey"
                    :class="{ 'disable': loading }"
                    @click="toggleModal()"
                    v-html="$t('global.cancel')"
                />
            </template>
        </modal>
    </div>
</template>

<script>
// package
import {
    reactive,
    toRefs,
    watch,
    computed
} from '@vue/composition-api';

// hooks
import tableStructure from '@/hooks/use-table';

// store
import { mapState, useStore } from '@/hooks/use-store';

export default {
    components: {
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        Tooltip: () => import( /* webpackChunkName: 'Tooltips' */ '@/components/bundles/tooltips/Tooltip' )
    },
    ...tableStructure,
    setup ( props ) {
        const texts = {
            active: {
                translate: 'activeToken',
                button: 'active',
                color: 'green'
            },
            delete: {
                translate: 'deleteToken',
                button: 'delete',
                color: 'red'
            },
            disable: {
                translate: 'disableToken',
                button: 'disable',
                color: 'red'
            }
        };

        const state = reactive({
            translate: '',
            buttonText: '',
            type: '',
            success: '',
            color: 'red',
            show: {
                modal: false,
                tooltip: false
            }
        });

        const { getters, actions } = useStore();

        // registo dos campos necessários para a
        // submissão para a API.
        const registerData = () => {
            const fields = [
                { name: 'id', value: props.row.id },
                ...( state.type !== 'delete'
                    ? [
                        { name: 'method', value: 'put' },
                        { name: 'revoked', value: !props.row.revoked ? 1 : 0 }
                    ]
                    : [ { name: 'endpoint', value: '/clients/personal-access-token' } ]
                )
            ];

            fields.forEach( field => {
                actions( 'registerField', field );
            });
        };

        const clearData = () => {
            if ( !state.success )
                actions( 'resetPayload' );

            state.success = '';
        };

        const toggleModal = type => {
            state.show.modal = !state.show.modal;

            // definição das configurações que irão
            // aparecer na modal
            // -- Titulo & texto
            // -- texto do botão
            // -- cor da modal
            state.translate = texts[type]?.translate;
            state.buttonText = texts[type]?.button;
            state.color = texts[type]?.color;
            state.type = type;

            // bloqueia a barra de adição & edição.
            actions( 'controlActionBar', { value: state.show.modal });

            // se acção for abrir a modal:
            // -- irá registar na store os valores necessários
            //    para serem enviados para API.
            // se ação for fechar a modal:
            // -- irá limpar da store os valores anteriormente
            //    adicionados.
            state.show.modal
                ? registerData()
                : clearData();
        };

        // obtenção do trigger
        const trigger = mapState({ module: 'storePayload', key: 'trigger' });

        // Watch para validar se houve alguma alteração numa
        // determinada linha, caso haja, significa que é necessário
        // fechar o modal.
        watch( () => trigger.value,
            newTrigger => {
                if ( newTrigger && state.show.modal )
                    state.show.modal = false;
            });

        // obtenção do loading da submissão
        const loading = mapState({ module: 'storePayload', key: 'loading' });

        // validação se esta no modo de edição ou não.
        const isInEditMode = computed( () => getters.isInEditMode );

        return {
            ...toRefs( state ),
            toggleModal,
            loading,
            isInEditMode
        };
    }
};
</script>